/** The tray's outer edge, which never collapses: one button per panel, plus the body toggle. */

import { Fragment, type ReactNode } from "react";

import { IconButton, Popover, Separator } from "@/components/ui";

import { Mono } from "../../components/Text";

import {
  AbilityIcon,
  AccessIcon,
  ActivityIcon,
  BindingsIcon,
  CanvasesIcon,
  CatalogIcon,
  CompositionIcon,
  CuesIcon,
  HistoryIcon,
  LayersIcon,
  NeedsIcon,
  SpaceIcon,
  StateIcon,
  TrayIcon,
} from "./TrayIcons";
import type { PanelEntry, PanelId } from "./panels";

function glyphOf(id: PanelId): ReactNode {
  switch (id) {
    case "space":
      return <SpaceIcon />;
    case "activity":
      return <ActivityIcon />;
    case "layers":
      return <LayersIcon />;
    case "bindings":
      return <BindingsIcon />;
    case "access":
      return <AccessIcon />;
    case "canvases":
      return <CanvasesIcon />;
    case "needs":
      return <NeedsIcon />;
    case "composition":
      return <CompositionIcon />;
    case "state":
      return <StateIcon />;
    case "ability":
      return <AbilityIcon />;
    case "cues":
      return <CuesIcon />;
    case "catalog":
      return <CatalogIcon />;
    case "history":
      return <HistoryIcon />;
  }
}

/** A question mark, for the trigger that opens the shortcut legend. */
function ShortcutsIcon() {
  return (
    <svg viewBox="0 0 16 16" width={14} height={14} aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M6 6.3c0-1.1 1-1.7 2-1.7s2 .6 2 1.7c0 1-.9 1.3-1.6 1.9-.4.4-.4.9-.4 1.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="11.4" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export interface PanelColumnProps {
  /** The panels the current subject is offered, in order. Each carries the key that opens it. */
  readonly offered: readonly PanelEntry[];
  readonly shown: PanelId;
  readonly open: boolean;
  readonly onPick: (id: PanelId) => void;
  readonly onToggle: () => void;
}

export function PanelColumn({ offered, shown, open, onPick, onToggle }: PanelColumnProps) {
  let lastGroup: PanelEntry["group"] | undefined;

  return (
    <nav className="cx-panels" aria-label="Editor panels">
      {offered.map((one) => {
        const divider = lastGroup !== undefined && lastGroup !== one.group;
        lastGroup = one.group;
        return (
          <Fragment key={one.id}>
            {divider ? <Separator /> : null}
            <IconButton
              size="sm"
              label={`${one.label} (${one.key})`}
              className={one.id === shown && open ? "cx-panel-pick cx-panel-pick-on" : "cx-panel-pick"}
              onClick={() => {
                onPick(one.id);
              }}
            >
              {glyphOf(one.id)}
            </IconButton>
          </Fragment>
        );
      })}
      <span className="cx-panels-gap" />
      <Popover
        label={
          <>
            <ShortcutsIcon />
            Keys
          </>
        }
        menuLabel="Panel shortcuts"
        side="top"
        triggerClassName="cx-shortcuts-trigger"
        minWidth={200}
      >
        {(close) => (
          <>
            {offered.map((one) => (
              <button
                key={one.id}
                type="button"
                className="al-popover-item"
                onClick={() => {
                  onPick(one.id);
                  close();
                }}
              >
                <span>{one.label}</span>
                <Mono>{one.key}</Mono>
              </button>
            ))}
          </>
        )}
      </Popover>
      <IconButton
        size="sm"
        label={open ? "Collapse the tray (backslash)" : "Pop out the tray (backslash)"}
        className="cx-panel-pick"
        onClick={onToggle}
      >
        <TrayIcon open={open} />
      </IconButton>
    </nav>
  );
}
